import { calculatePolygonCentroid } from 'components/BaseMap/util/BaseMap.utils';
import {
  FEMA_ENVIRONMENT_CHOICES,
  type FemaEnvironmentChoice,
} from 'features/Weather/features/WeatherAlerts/features/AlertCreator/components/IpawsAlertFields/config/femaEnvironment.config';
import type { FemaAlertInfo, FemaAlertPayload } from 'types/fema.types';
import type { FemaMessageThread } from 'types/femaMessage.types';
import {
  deriveFemaThreadState,
  getLatestVersion,
  getRecordDescription,
  getRecordExpires,
  getRecordHeadline,
  getRecordIdentifier,
  getRecordSentAt,
} from './femaMessage.utils';
import { FEMA_STATE_BADGE_VARIANT, FEMA_STATE_LABEL } from './femaMessageDisplay.utils';
export { isLegacyFemaAlert } from 'features/Weather/features/WeatherAlerts/utils/isFemaAlert.utils';

export type FemaAlertCardData = {
  message_id: string;
  description: string;
  message: string;
  smpp_message: string;
  nws_office: string;
  severity: number;
  severityLabel: string;
  statusLabel: string;
  statusBadgeVariant: string;
  issue_time_utc: string;
  broadcast_time_utc: string;
  valid_until_time_utc: string;
  workplaceId: number;
  globalActionId?: number | string | null;
  /** The class badge text. Absent when the row carries no known class. */
  environmentLabel?: string;
  /** The class badge variant. Absent when the row carries no known class. */
  environmentBadgeVariant?: FemaEnvironmentBadgeVariant;
  can_update?: boolean;
  areaDescription?: string;
  femaInfo?: FemaAlertInfo[];
  femaThread?: FemaMessageThread;
  processedCenter?: [number, number];
  processedPolygon?: [number, number][][];
  processedBounds?: [[number, number], [number, number]];
  processedCircles?: { center: [number, number]; radius: number }[];
};

export type FemaStoredAlertRow = {
  id?: string | number;
  message_id?: string | number;
  workplace_id?: number;
  user_id?: number;
  cog_id?: string;
  /** `Ipaws_Message.environment` (migration 010). `null` or absent on a row written before 010. */
  environment?: FemaEnvironmentChoice | null;
  cap_identifier: string;
  status?: string;
  msg_type?: string;
  scope?: string;
  state?: string;
  linked_alert_id?: string | number | null;
  can_update?: boolean;
  global_action_id?: number | string | null;
  created_at?: string;
  serialized_at?: string;
  source_value?: string;
  message?: FemaAlertPayload & {
    identifier?: string;
    sender?: string;
    sent?: string;
    source?: string;
  };
  xml?: string | null;
};

export type FemaStoredAlertCardData = FemaAlertCardData & {
  alertSource: 'fema';
  rawFemaRecord: FemaStoredAlertRow;
};

const FEMA_SEVERITY_VALUES: Record<string, number> = {
  Extreme: 80,
  Severe: 40,
  Moderate: 20,
  Minor: 0,
  Unknown: 0,
};

const FEMA_STATE_LABELS: Record<string, string> = {
  cancelled: 'Cancelled',
  failed: 'Failed',
  sent: 'Sent',
  submitted: 'Submitted',
  updated: 'Updated',
};

export type FemaEnvironmentBadgeVariant = 'secondary' | 'info' | 'danger';

/**
 * The class badge on a FEMA list card. LIVE PRODUCTION uses `danger`, so that it is different
 * from the two test classes.
 */
export const FEMA_ENVIRONMENT_BADGE: Record<
  FemaEnvironmentChoice,
  { label: string; variant: FemaEnvironmentBadgeVariant }
> = {
  tdl: { label: 'TDL', variant: 'secondary' },
  demo: { label: 'DEMO', variant: 'info' },
  production: { label: 'LIVE PRODUCTION', variant: 'danger' },
};

/**
 * Reads the class token of a FEMA message row.
 *
 * @param value The `environment` value of a row, as the backend sent it.
 * @returns The class token, or null when the value is absent or not a known class.
 */
export const parseFemaMessageEnvironment = (value: unknown): FemaEnvironmentChoice | null =>
  FEMA_ENVIRONMENT_CHOICES.find((choice) => choice === value) ?? null;

/**
 * Gives the class badge fields for a card.
 *
 * @param value The `environment` value of a row, as the backend sent it.
 * @returns The label and the variant, or no field when the row carries no known class.
 */
export const getFemaEnvironmentBadgeFields = (
  value: unknown,
): Pick<FemaAlertCardData, 'environmentLabel' | 'environmentBadgeVariant'> => {
  const environment = parseFemaMessageEnvironment(value);
  if (!environment) return {};
  const badge = FEMA_ENVIRONMENT_BADGE[environment];
  return { environmentLabel: badge.label, environmentBadgeVariant: badge.variant };
};

const getAreaDescription = (info?: FemaAlertInfo): string | undefined =>
  info?.area
    ?.map((area) => area.areaDesc)
    .filter((areaDescription): areaDescription is string => Boolean(areaDescription))
    .join(', ') || undefined;

/**
 * Identifies a raw FEMA message-store row embedded in the all-alerts response.
 *
 * @param value A raw alert record returned by the backend.
 * @returns True when the record has the FEMA CAP identifier required for adaptation.
 */
export const isFemaStoredAlertRow = (value: unknown): value is FemaStoredAlertRow => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  return (
    (typeof candidate.id === 'string' ||
      typeof candidate.id === 'number' ||
      typeof candidate.message_id === 'string' ||
      typeof candidate.message_id === 'number') &&
    typeof candidate.cap_identifier === 'string' &&
    candidate.cap_identifier.length > 0
  );
};

/**
 * Converts a partial FEMA message-store row into the shared alert-card shape.
 *
 * @param record A FEMA row embedded in the all-alerts response.
 * @returns Alert-card data with the raw FEMA row retained for later details and actions.
 */
export const femaStoredAlertRowToAlert = (record: FemaStoredAlertRow): FemaStoredAlertCardData => {
  const payload = record.message;
  const info = payload?.info?.[0];
  const state = record.state?.toLowerCase();
  const timestamp = record.created_at ?? '';
  const messageType = payload?.msgType ?? record.msg_type ?? 'Alert';
  const headline = info?.headline ?? info?.event ?? `FEMA ${messageType}`;
  const message = info?.description ?? info?.instruction ?? headline;
  const severity = FEMA_SEVERITY_VALUES[info?.severity ?? 'Unknown'] ?? 0;
  const storedThread = femaStoredAlertRowToThread(record);

  return {
    message_id: record.cap_identifier,
    description: headline,
    message,
    smpp_message: headline,
    nws_office: info?.senderName ?? (record.cog_id ? `FEMA COG ${record.cog_id}` : 'FEMA IPAWS'),
    severity,
    severityLabel: info?.severity ?? 'Unknown',
    statusLabel: FEMA_STATE_LABELS[state ?? ''] ?? record.state ?? record.status ?? 'Submitted',
    statusBadgeVariant:
      state === 'failed' ? 'danger' : state === 'cancelled' ? 'secondary' : 'info',
    issue_time_utc: timestamp,
    broadcast_time_utc: timestamp,
    valid_until_time_utc: info?.expires ? String(info.expires) : '',
    workplaceId: record.workplace_id ?? 0,
    globalActionId: record.global_action_id ?? null,
    can_update: record.can_update,
    ...getFemaEnvironmentBadgeFields(record.environment),
    areaDescription: getAreaDescription(info),
    femaInfo: payload?.info,
    femaThread: storedThread ?? undefined,
    alertSource: 'fema',
    rawFemaRecord: record,
    ...(storedThread ? getPolygonData(storedThread) : {}),
  };
};

/**
 * Converts one stored all-alerts FEMA row into the thread shape used by FEMA actions.
 *
 * @param record A FEMA row returned by the unified alerts endpoint.
 * @returns A single-version FEMA thread, or null when the CAP payload is unavailable.
 */
export const femaStoredAlertRowToThread = (
  record: FemaStoredAlertRow,
): FemaMessageThread | null => {
  if (!record.message) return null;

  const recordId = record.id ?? record.message_id;
  if (recordId === undefined) return null;

  return {
    rootIdentifier: record.cap_identifier,
    versions: [
      {
        id: recordId,
        workplaceId: record.workplace_id ?? 0,
        cogId: record.cog_id,
        global_action_id: record.global_action_id ?? null,
        environment: parseFemaMessageEnvironment(record.environment),
        envelope: {
          sender: record.message.sender ?? record.cog_id ?? 'FEMA IPAWS',
          sent: record.message.sent ?? record.serialized_at ?? record.created_at ?? '',
          source: record.message.source ?? record.source_value ?? 'all-alerts',
        },
        payload: {
          ...record.message,
          identifier: record.cap_identifier,
        },
        rawCapXml: record.xml ?? null,
      },
    ],
  };
};

const getPolygonData = (thread: FemaMessageThread) => {
  const latest = getLatestVersion(thread);
  const areas = latest?.payload.info?.flatMap((info) => info.area ?? []) ?? [];
  const processedCircles = areas.flatMap((area) =>
    (area.circle ?? []).map((circle) => ({
      center: [circle.center.latitude, circle.center.longitude] as [number, number],
      radius: circle.radius * 1000,
    })),
  );
  const processedPolygon = areas.flatMap((area) =>
    (area.polygon ?? []).map((ring) =>
      ring.map(({ latitude, longitude }) => [latitude, longitude] as [number, number]),
    ),
  );

  if (!processedPolygon.length && !processedCircles.length) return {};

  const coordinates = processedPolygon.flat();
  const latitudes = coordinates.map(([latitude]) => latitude);
  const longitudes = coordinates.map(([, longitude]) => longitude);
  const polygonCenter = processedPolygon.length
    ? calculatePolygonCentroid(processedPolygon[0])
    : null;
  const center = polygonCenter
    ? ([polygonCenter.lat, polygonCenter.lng] as [number, number])
    : processedCircles[0]?.center;

  return {
    processedCenter: center,
    processedPolygon: processedPolygon.length ? processedPolygon : undefined,
    processedBounds: processedPolygon.length
      ? ([
          [Math.min(...latitudes), Math.min(...longitudes)],
          [Math.max(...latitudes), Math.max(...longitudes)],
        ] as [[number, number], [number, number]])
      : undefined,
    processedCircles: processedCircles.length ? processedCircles : undefined,
  };
};

/**
 * Gives the class of a FEMA thread. An Update and a Cancel keep the class of the message they
 * name, so every version with a class has the same class. The newest version with a class wins.
 *
 * @param thread The FEMA thread.
 * @returns The class token, or null when no version carries a known class.
 */
export const getThreadEnvironment = (thread: FemaMessageThread): FemaEnvironmentChoice | null => {
  for (let index = thread.versions.length - 1; index >= 0; index -= 1) {
    const environment = parseFemaMessageEnvironment(thread.versions[index].environment);
    if (environment) return environment;
  }
  return null;
};

/**
 * Convert a FEMA message thread into the data shape consumed by the shared AlertCard and AlertMap.
 *
 * @param thread The FEMA thread to summarize using its latest version.
 * @returns The shared alert-card data, or null when the thread has no versions.
 */
export const femaThreadToAlert = (thread: FemaMessageThread): FemaAlertCardData | null => {
  const latest = getLatestVersion(thread);
  if (!latest) return null;
  const globalActionRecord = [...thread.versions]
    .reverse()
    .find((version) => version.global_action_id != null);

  const info = latest.payload.info?.[0];
  const headline = getRecordHeadline(latest) ?? 'Untitled FEMA message';
  const description = getRecordDescription(latest) ?? headline;
  const severity = FEMA_SEVERITY_VALUES[info?.severity ?? 'Unknown'] ?? 0;
  const state = deriveFemaThreadState(thread);
  const sentAt = getRecordSentAt(latest);
  const expires = getRecordExpires(latest) ?? sentAt;

  return {
    message_id: getRecordIdentifier(latest) || thread.rootIdentifier,
    description: headline,
    message: description,
    smpp_message: headline,
    nws_office: latest.envelope.sender,
    severity,
    severityLabel: info?.severity ?? 'Unknown',
    statusLabel: FEMA_STATE_LABEL[state],
    statusBadgeVariant: FEMA_STATE_BADGE_VARIANT[state],
    issue_time_utc: sentAt,
    broadcast_time_utc: sentAt,
    valid_until_time_utc: expires,
    workplaceId: latest.workplaceId,
    globalActionId: globalActionRecord?.global_action_id ?? null,
    ...getFemaEnvironmentBadgeFields(getThreadEnvironment(thread)),
    areaDescription: getAreaDescription(info),
    femaInfo: latest.payload.info,
    femaThread: thread,
    ...getPolygonData(thread),
  };
};
